<script setup lang="ts">
import { CalendarIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import type { TMode } from '~/interfaces/components/date-picker';

defineProps<{
  bgColor: string;
  disabled: boolean;
  displayRangeEnd: string;
  displayRangeStart: string;
  displaySingle: string;
  highlightEnd: boolean;
  highlightSingle: boolean;
  highlightStart: boolean;
  hideResetOverRide: boolean;
  mode: TMode;
  open: boolean;
  showReset: boolean;
}>();

const emits = defineEmits<{ (e: 'reset'): void; (e: 'toggle'): void }>();
</script>

<template>
  <div class="flex gap-2 items-center disabled:cursor-not-allowed">
    <DesignSystemButton
      v-if="mode === 'single'"
      class="w-full stacked-border rounded-lg flex items-center justify-between gap-4 px-4 font-light disabled:opacity-50 disabled:cursor-not-allowed disabled:!bg-transparent truncate"
      :class="[bgColor]"
      type="transparent"
      padding="lg"
      rounding="lg"
      aria-haspopup="dialog"
      :aria-expanded="open"
      aria-controls="date-picker-panel"
      :disabled="disabled"
      :title="displaySingle"
      @click="emits('toggle')"
    >
      <div class="text-sm capitalize">{{ displaySingle }}</div>
      <CalendarIcon class="!w-4 !h-4 stroke-2" />
    </DesignSystemButton>

    <div v-else class="flex w-full">
      <DesignSystemButton
        class="w-1/2 stacked-border rounded-r-none flex items-center justify-center gap-4 px-4 font-light disabled:opacity-50 disabled:cursor-not-allowed disabled:!bg-transparent truncate"
        type="transparent"
        :class="[
          bgColor,
          {
            '!border-primary-blue-500': highlightStart || open,
          },
        ]"
        aria-haspopup="dialog"
        :aria-expanded="open"
        aria-controls="date-picker-panel"
        padding="lg"
        rounding="lg"
        :disabled="disabled"
        :title="displayRangeStart"
        @click="emits('toggle')"
      >
        <CalendarIcon class="!w-4 !h-4 stroke-2" />
        <div class="text-xs capitalize">{{ displayRangeStart }}</div>
      </DesignSystemButton>
      <DesignSystemButton
        class="w-1/2 stacked-border rounded-l-none flex items-center justify-center gap-4 px-4 font-light disabled:opacity-50 disabled:cursor-not-allowed disabled:!bg-transparent truncate"
        type="transparent"
        :class="[
          bgColor,
          {
            '!border-primary-blue-500': highlightEnd,
          },
        ]"
        aria-haspopup="dialog"
        :aria-expanded="open"
        aria-controls="date-picker-panel"
        padding="lg"
        rounding="lg"
        :disabled="disabled"
        :title="displayRangeEnd"
        @click="emits('toggle')"
      >
        <CalendarIcon class="!w-4 !h-4 stroke-2" />
        <div class="text-xs capitalize">{{ displayRangeEnd }}</div>
      </DesignSystemButton>
    </div>

    <DesignSystemButton
      v-if="showReset && !hideResetOverRide"
      type="transparent"
      class="disabled:opacity-30 h-2/3"
      :class="[bgColor]"
      :disabled="disabled"
      shape="circle"
      padding="sm"
      :title="$t('reset')"
      @click.stop="emits('reset')"
    >
      <XMarkIcon class="stroke-2 !h-5 !w-5" />
    </DesignSystemButton>
  </div>
</template>
